import React, {
  Fragment,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useId,
  useState,
} from 'react';
import clsx from 'clsx';
import { useSettings } from '../../../../hooks/useSettings';
import Star from './components/Star';
import { useTranslations } from '../../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickTypes,
  NarvarClickSubTypes,
} from '../../../../hooks/Analytics';
import styles from './StarRating.module.css';
import RatingText from './components/RatingText';

const TOTAL_STARS = 5;

export interface StarRatingProps {
  onClick?: (
    event: MouseEvent<HTMLInputElement>,
    index: number,
    selectedText?: string,
  ) => void;
  onKeyDown?: (
    event: KeyboardEvent,
    index: number,
    selectedText?: string,
  ) => void;
  selectedRating: number;
  featureName?: string;
  isLandscape?: boolean;
  displayTextFirst?: boolean;
}

const StarRating = ({
  onClick,
  onKeyDown,
  selectedRating,
  featureName,
  isLandscape = false,
  displayTextFirst = false,
}: StarRatingProps) => {
  const ratingGroupId = useId();

  const { feedback } = useSettings();

  const { enableStarRatingText: enableStarRatingTextIn } = feedback || {};
  const enableStarRatingText =
    typeof enableStarRatingTextIn === 'boolean' ? enableStarRatingTextIn : true;

  const {
    feedbackRatingShortOne,
    feedbackRatingShortTwo,
    feedbackRatingShortThree,
    feedbackRatingShortFour,
    feedbackRatingShortFive,
    feedbackRatingSubcaption: feedbackRatingSubcaptionIn,
  } = useTranslations();

  const feedbackRatingSubcaption = feedbackRatingSubcaptionIn ?? '';

  const [hovered, setHovered] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const logEvent = useAnalyticsPost();

  const ratingText = [
    feedbackRatingShortOne,
    feedbackRatingShortTwo,
    feedbackRatingShortThree,
    feedbackRatingShortFour,
    feedbackRatingShortFive,
  ].map((text) => text ?? '');

  const highlightRating = (rating: number) => {
    setHovered(true);
    setHoverRating(rating);
  };

  const clearHighlight = () => {
    setHovered(false);
    setHoverRating(0);
  };

  const selectedTextFor = (rating: number) =>
    `${feedbackRatingSubcaption} ${ratingText[rating - 1].toLowerCase()}?`;

  const handleClick = (event: MouseEvent<HTMLInputElement>, rating: number) => {
    logEvent({
      clickCategory: NarvarClickSubTypes.RATING,
      clickSubType: NarvarClickSubTypes.RATING,
      clickSurveyRating: rating,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_BLOCK_STAR_RATING,
      eventFeature: NarvarEventComponents.NTH_BLOCK_STAR_RATING,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BLOCK_STAR_RATING,
    });
    if (onClick) onClick(event, rating, selectedTextFor(rating));
  };

  const handleKeyDown = (event: KeyboardEvent, rating: number) => {
    if (!onKeyDown) return;
    onKeyDown(event, rating, selectedTextFor(rating));
  };

  const ratingNumber = hovered ? hoverRating : selectedRating;

  const stars = (
    <div className={styles.fieldSetContainer}>
      <fieldset className={styles.styledFieldset}>
        {Array.from({ length: TOTAL_STARS }, (_, index) => (
          <span key={`star-rating-${index + 1}`}>
            <input
              className={styles.visuallyHiddenInput}
              type="radio"
              id={`${ratingGroupId}-star-${index + 1}`}
              name={ratingGroupId}
              value={index + 1}
              onFocus={() => highlightRating(index + 1)}
              onBlur={clearHighlight}
              onKeyDown={(event) => handleKeyDown(event, index + 1)}
              onClick={(event) => handleClick(event, index + 1)}
              aria-label={ratingText[index]}
              data-test={`starRatingIconButton${index + 1}`}
            />
            <label
              className={styles.inputLabel}
              htmlFor={`${ratingGroupId}-star-${index + 1}`}
              onMouseEnter={() => highlightRating(index + 1)}
              onMouseLeave={clearHighlight}
            >
              <Star
                full={
                  hovered
                    ? hoverRating >= index + 1
                    : selectedRating >= index + 1
                }
                data-test={`starRatingIconStar${index + 1}`}
              />
            </label>
          </span>
        ))}
      </fieldset>
    </div>
  );

  const text: ReactElement = (
    <RatingText
      ratingText={ratingText}
      ratingNumber={ratingNumber}
      selectedRating={selectedRating}
      isLandscape={isLandscape}
      feedbackRatingSubcaption={feedbackRatingSubcaption}
    />
  );

  const starsClasses = clsx({
    [styles.stars]: true,
    [styles.textFirst]: displayTextFirst,
  });
  if (isLandscape) {
    const starsAndText = [
      <div key="stars" className={styles.stars}>
        {stars}
      </div>,
      <Fragment key="text">
        {enableStarRatingText && <div className={starsClasses}>{text}</div>}
      </Fragment>,
    ];
    if (displayTextFirst) starsAndText.reverse();
    return <div>{starsAndText}</div>;
  }

  return (
    <div className={styles.center}>
      {stars}
      {enableStarRatingText && <div className={styles.ratingText}>{text}</div>}
    </div>
  );
};

export default StarRating;
